import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, BarChart3, Shield, CheckCircle, Type, Sparkles, FileText, Zap } from "lucide-react";

const IMG = {
  dashboard: "/assets/changelog/dashboard.png",
  onboarding: "/assets/changelog/onboarding.png",
  validation: "/assets/changelog/validation.png",
  accents: "/assets/changelog/accents.png",
};

const RELEASES = [
  {
    version: "1.2.0",
    date: "18 de marzo de 2026",
    title: "Correcciones de idioma y pulido general",
    entries: [
      {
        icon: Type,
        color: "#883476",
        title: "Correcciones de acentos en español",
        description: "Revisión exhaustiva de 27 archivos con 81 correcciones de acentos, signos de exclamación invertidos (¡) e interrogación (¿), y actualización de opciones \"Sí\" en formularios.",
        image: IMG.accents,
        details: [
          "Formularios F1-F5: física, sueño, estrés, médica, nutrición, etc.",
          "Dashboard y Recomendaciones: presión arterial, oximetría, señales de alerta",
          "SVG Illustrations: títulos de gráficos y mensajes de completado",
          "Preguntas con signos ¿? y exclamaciones con ¡ agregados",
        ],
      },
    ],
  },
  {
    version: "1.1.0",
    date: "18 de marzo de 2026",
    title: "Dashboard visual, onboarding y validaciones",
    entries: [
      {
        icon: BarChart3,
        color: "#00628F",
        title: "4 gráficos dinámicos en el Dashboard",
        description: "Visualización completa de las 5 dimensiones de salud con gráficos interactivos que se alimentan de los datos del usuario.",
        image: IMG.dashboard,
        details: [
          "Radar Pentagonal: 5 dimensiones normalizadas 0-100 con animación",
          "Gauge IMC: semicírculo con 4 zonas de color y aguja animada",
          "Donut Dieta: anillo con score/40 y clasificación centrada",
          "Termómetro Emocional: barra vertical con 5 zonas de color",
        ],
      },
      {
        icon: Sparkles,
        color: "#2A9D8F",
        title: "Onboarding para nuevos usuarios",
        description: "Tour guiado de 4 pantallas con ilustraciones SVG animadas que introduce a los nuevos usuarios a las funcionalidades de la app.",
        image: IMG.onboarding,
        details: [
          "Bienvenida con escudo y checkmark",
          "5 Dimensiones de salud con iconos animados",
          "Vista previa de resultados inmediatos",
          "Privacidad con links legales",
        ],
      },
      {
        icon: Shield,
        color: "#C0453A",
        title: "Validación inline en autenticación",
        description: "Feedback inmediato al usuario en todas las pantallas del ciclo de login: email, contraseña, registro y vinculación de cuenta.",
        image: IMG.validation,
        details: [
          "Email vacío/inválido: mensajes contextuales",
          "Contraseña corta: \"Mínimo 8 caracteres\"",
          "Contraseñas no coinciden: alerta visual",
          "Errores sin empujar contenido (position absolute)",
        ],
      },
      {
        icon: Zap,
        color: "#F9A01B",
        title: "Mejoras de navegación y UX",
        description: "Múltiples mejoras para facilitar la navegación dentro de la app.",
        details: [
          "Botón \"Mi panel\" en todos los formularios F1-F5",
          "Links legales en Login, Onboarding y Consentimiento",
          "Documentación de testing (19 tests de auth)",
        ],
      },
    ],
  },
  {
    version: "1.0.0",
    date: "16 de marzo de 2026",
    title: "Lanzamiento inicial",
    entries: [
      {
        icon: CheckCircle,
        color: "#157346",
        title: "App completa con Firebase",
        description: "Primera versión funcional con autenticación completa (Google, Email, Invitado), 5 formularios de evaluación, dashboard y recomendaciones.",
        details: [
          "Autenticación: Google, Email/Password, Invitado, cuenta Demo",
          "Formularios F1-F5: Demográfico, Físico, IPAQ, Nutrición, Emocional",
          "Dashboard con alertas y resumen de evaluaciones",
          "Sistema de medicamentos con tags dinámicos",
        ],
      },
      {
        icon: FileText,
        color: "#71644C",
        title: "Base de datos y persistencia",
        description: "Integración completa con Firestore para almacenar perfiles de usuario con todos los resultados de evaluaciones.",
        details: [
          "Firestore: colección users/{uid} con perfil completo",
          "Cuenta demo \"José Pérez\" con datos pre-llenados",
          "Vinculación de cuentas anónimas a permanentes",
        ],
      },
    ],
  },
];

function EntryCard({ entry }) {
  const Icon = entry.icon;
  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{ background: "white", border: "1px solid #E2D9CB" }}
      data-testid={`changelog-entry-${entry.title.slice(0, 20).replace(/\s/g, "-").toLowerCase()}`}
    >
      {entry.image && (
        <div style={{ background: `${entry.color}10`, padding: "16px 16px 8px" }}>
          <img
            src={entry.image}
            alt={entry.title}
            className="w-full rounded-xl"
            style={{ maxHeight: 200, objectFit: "cover" }}
            loading="lazy"
          />
        </div>
      )}
      <div className="p-4">
        <div className="flex items-start gap-3 mb-2">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: `${entry.color}15` }}
          >
            <Icon size={16} style={{ color: entry.color }} />
          </div>
          <div>
            <h3 className="text-sm font-bold" style={{ color: "#2D2A24" }}>{entry.title}</h3>
            <p className="text-xs mt-1 leading-relaxed" style={{ color: "#71644C" }}>{entry.description}</p>
          </div>
        </div>
        {entry.details && (
          <ul className="mt-3 space-y-1.5 pl-11">
            {entry.details.map((d, i) => (
              <li key={i} className="text-xs flex items-start gap-2" style={{ color: "#71644C" }}>
                <span className="mt-1.5 w-1 h-1 rounded-full flex-shrink-0" style={{ background: entry.color }} />
                {d}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default function Changelog() {
  const navigate = useNavigate();
  const location = useLocation();

  const handleBack = () => {
    if (location.state?.from) {
      navigate(location.state.from);
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "#FCFAEE", fontFamily: "Inter, sans-serif" }}>
      {/* Header */}
      <div
        className="sticky top-0 z-10 px-4 py-3 flex items-center gap-3"
        style={{ background: "#FCFAEE", borderBottom: "1px solid #E2D9CB" }}
      >
        <button
          onClick={handleBack}
          className="flex items-center gap-1 text-xs font-semibold"
          style={{ color: "#00628F", background: "none", border: "none", cursor: "pointer" }}
          data-testid="changelog-back-btn"
        >
          <ArrowLeft size={16} />
          Volver
        </button>
        <span className="text-sm font-bold" style={{ color: "#2D2A24" }}>Novedades</span>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Page title */}
        <div className="text-center mb-8">
          <div
            className="w-12 h-12 rounded-2xl mx-auto mb-3 flex items-center justify-center"
            style={{ background: "#00628F" }}
          >
            <Sparkles size={22} color="white" />
          </div>
          <h1 className="text-xl font-bold" style={{ color: "#2D2A24" }}>Novedades de We360</h1>
          <p className="text-xs mt-1" style={{ color: "#71644C" }}>Mejoras y nuevas funcionalidades</p>
        </div>

        {/* Releases */}
        <div className="space-y-8">
          {RELEASES.map((release) => (
            <div key={release.version} data-testid={`release-${release.version}`}>
              {/* Version header */}
              <div className="flex items-center gap-3 mb-4">
                <span
                  className="text-xs font-bold px-2.5 py-1 rounded-full"
                  style={{ background: "#00628F", color: "white" }}
                >
                  v{release.version}
                </span>
                <span className="text-xs font-medium" style={{ color: "#71644C" }}>{release.date}</span>
              </div>
              <p className="text-sm font-semibold mb-3" style={{ color: "#2D2A24" }}>{release.title}</p>

              {/* Entries */}
              <div className="space-y-3">
                {release.entries.map((entry, i) => (
                  <EntryCard key={i} entry={entry} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="text-center mt-10 pb-6">
          <p className="text-xs" style={{ color: "#B5A998" }}>
            We360 &middot; Salud preventiva para tu comunidad
          </p>
        </div>
      </div>
    </div>
  );
}
